/**
 * Stops a tab that keeps reloading itself from booting again.
 *
 * WHY. 2026-10-29: one cloud.zezamii.com tab, signed in as a shared service account, fully loaded about
 * 1,650 times in a morning — every 2.5 to 11 seconds, each load firing the ~30 boot requests. That was
 * about 80% of production API traffic. Whatever drove it (an extension, a script, a stuck redirect),
 * the dashboard had no way to notice it was being loaded again and again, so every load cost the full
 * boot.
 *
 * Each page load records a timestamp in sessionStorage, which is per TAB and survives reloads. When the
 * tab has loaded more than MAX_LOADS times inside WINDOW_MS, the app does not boot: it shows a screen
 * that says so and makes no API calls until someone presses Continue.
 *
 * The threshold leaves room for every legitimate burst: a Kinde sign-in is about three loads (sign-in →
 * Kinde → /callback → home), the deploy-chunk recovery in ErrorBoundary is one per 10 s, and a person
 * pressing F5 a few times stays well under it. The loop it is for ran at 6 to 20 loads a minute.
 *
 * Fails open: if sessionStorage is unavailable the guard never trips.
 */

export const RELOAD_LOOP_STORAGE_KEY = 'pageLoadTimes';
export const MAX_LOADS = 8;
export const WINDOW_MS = 60000;

const readTimes = (storage) => {
	try {
		const parsed = JSON.parse(storage.getItem(RELOAD_LOOP_STORAGE_KEY) || '[]');
		return Array.isArray(parsed) ? parsed.filter((t) => Number.isFinite(t)) : [];
	} catch {
		return [];
	}
};

/**
 * Record this page load and say whether the tab is in a reload loop.
 * @returns {{tripped: boolean, loads: number}}
 */
export function recordPageLoad({ now = Date.now(), storage = globalThis.sessionStorage } = {}) {
	try {
		if (!storage) return { tripped: false, loads: 0 };
		const times = [...readTimes(storage).filter((t) => now - t < WINDOW_MS), now];
		storage.setItem(RELOAD_LOOP_STORAGE_KEY, JSON.stringify(times));
		return { tripped: times.length > MAX_LOADS, loads: times.length };
	} catch {
		return { tripped: false, loads: 0 };
	}
}

/** Forget the recorded loads, so the next load boots normally (the Continue button). */
export function clearPageLoads({ storage = globalThis.sessionStorage } = {}) {
	try {
		storage?.removeItem(RELOAD_LOOP_STORAGE_KEY);
	} catch {
		// Nothing to clear without storage; the guard was never able to trip.
	}
}
